import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Reveal from "@/components/base/Reveal";
import StarRating from "@/components/base/StarRating";
import { useVehicle } from "@/hooks/useVehicle";
import { formatKES, vehicleTitle } from "@/lib/format";
import Gallery from "./components/Gallery";
import SpecsGrid from "./components/SpecsGrid";
import FeaturesList from "./components/FeaturesList";
import HostCard from "./components/HostCard";
import ReviewsList from "./components/ReviewsList";
import AvailabilityCalendar from "./components/AvailabilityCalendar";
import BookingPanel from "./components/BookingPanel";

interface SelectedRange {
  start: string | null;
  end: string | null;
}

export default function VehicleDetail() {
  const { id } = useParams();
  const vehicleId = id ? Number(id) : null;
  const { vehicle, reviews, bookedRanges, loading, error, reload } = useVehicle(vehicleId);
  const [range, setRange] = useState<SelectedRange>({ start: null, end: null });

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-background-50">
        <Navbar />
        <div className="mx-auto w-full max-w-7xl px-4 pt-28 md:px-8 md:pt-36">
          <div className="h-[280px] w-full animate-pulse rounded-2xl bg-background-100 md:h-[460px]" />
          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_400px]">
            <div className="space-y-5">
              <div className="h-8 w-1/2 animate-pulse rounded bg-background-100" />
              <div className="h-24 w-full animate-pulse rounded-2xl bg-background-100" />
              <div className="h-40 w-full animate-pulse rounded-2xl bg-background-100" />
            </div>
            <div className="h-80 w-full animate-pulse rounded-2xl bg-background-100" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen w-full bg-background-50">
        <Navbar />
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 pt-40 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
            <i className="ri-roadster-line"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
            {error || "This car isn't available"}
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            It may have been taken down, or the link is out of date.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={reload}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-5 py-2.5 text-sm font-medium text-foreground-900"
            >
              <i className="ri-refresh-line"></i> Try again
            </button>
            <Link
              to="/browse"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50"
            >
              <i className="ri-search-eye-line"></i> Browse all cars
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const title = vehicleTitle(vehicle.make, vehicle.model, vehicle.year);

  return (
    <div className="min-h-screen w-full bg-background-50">
      <Navbar />

      <div className="mx-auto w-full max-w-7xl px-4 pt-24 md:px-8 md:pt-32">
        <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-foreground-900">
            Home
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/browse" className="transition-colors hover:text-foreground-900">
            Browse cars
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link
            to={`/browse?city=${encodeURIComponent(vehicle.city)}`}
            className="transition-colors hover:text-foreground-900"
          >
            {vehicle.city}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="truncate text-foreground-800">{title}</span>
        </nav>

        <Reveal>
          <Gallery images={vehicle.images} title={title} />
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-8 pb-20 lg:grid-cols-[1fr_400px]">
          <div className="space-y-10">
            <Reveal>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h1 className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">{title}</h1>
                  <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
                    <StarRating rating={Number(vehicle.rating ?? 0)} count={vehicle.reviews_count} showValue />
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-map-pin-2-line text-primary-500"></i>
                      {vehicle.location || vehicle.city}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-roadster-line text-primary-500"></i>
                      {vehicle.category || vehicle.body_type}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {vehicle.instant_book && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-accent-500/15 px-3 py-1.5 text-xs font-medium text-accent-300">
                      <i className="ri-flashlight-fill"></i> Instant book
                    </span>
                  )}
                  {vehicle.delivery && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary-500/20 px-3 py-1.5 text-xs font-medium text-secondary-300">
                      <i className="ri-map-pin-2-line"></i> Delivery available
                    </span>
                  )}
                </div>
              </div>
            </Reveal>

            <Reveal>
              <h2 className="mb-4 font-heading text-lg font-semibold text-foreground-950">Car specs</h2>
              <SpecsGrid vehicle={vehicle} />
            </Reveal>

            <Reveal>
              <h2 className="mb-4 font-heading text-lg font-semibold text-foreground-950">About this car</h2>
              <p className="text-sm leading-relaxed text-foreground-600">
                {vehicle.description ||
                  `Meet the ${title} — a well-kept ${vehicle.category?.toLowerCase() || "car"} available for hire in ${vehicle.city}. Your host ${vehicle.owner_name || "a verified Gari host"} keeps it serviced, clean and ready for your next trip.`}
              </p>
            </Reveal>

            <Reveal>
              <h2 className="mb-4 font-heading text-lg font-semibold text-foreground-950">What&apos;s included</h2>
              <FeaturesList features={vehicle.features} />
            </Reveal>

            <Reveal>
              <div id="availability" className="scroll-mt-28">
                <h2 className="mb-4 font-heading text-lg font-semibold text-foreground-950">Availability</h2>
                <AvailabilityCalendar bookedRanges={bookedRanges} range={range} onChange={setRange} />
              </div>
            </Reveal>

            <Reveal>
              <h2 className="mb-4 font-heading text-lg font-semibold text-foreground-950">Meet your host</h2>
              <HostCard vehicle={vehicle} />
            </Reveal>

            <Reveal>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-lg font-semibold text-foreground-950">
                  Rider reviews {reviews.length ? `(${reviews.length})` : ""}
                </h2>
              </div>
              <ReviewsList reviews={reviews} />
            </Reveal>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <BookingPanel vehicle={vehicle} start={range.start} end={range.end} />

            <div className="mt-4 rounded-2xl border border-background-200/70 bg-background-100 p-5">
              <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                <i className="ri-information-line text-primary-500"></i> Good to know
              </p>
              <ul className="mt-3 space-y-2.5 text-sm text-foreground-600">
                <li className="flex items-start gap-2">
                  <i className="ri-time-line mt-0.5 text-foreground-500"></i> Daily rate is for 24 hours from pick-up
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-gas-station-line mt-0.5 text-foreground-500"></i> Return with the same fuel level
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-user-shared-line mt-0.5 text-foreground-500"></i> Additional drivers must be added at booking
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}